import type { AuthSession } from './types'

/**
 * Framework-agnostic client-side auth session state. This is the ONE place
 * the SPA holds anything about "am I logged in" — deliberately not React
 * state, so the authed-fetch middleware (apps/web/src/lib/api/client.ts),
 * which runs outside any component tree, can read/mutate it directly and the
 * React context (./auth-context.tsx) is just a thin subscriber.
 *
 * Holds only what `GET /auth/session` returns (user id/email/display name,
 * the server-derived AccountContext, and the CSRF double-submit token) — no
 * OIDC access/refresh tokens ever pass through here (C22); those never leave
 * the BFF. This module also never touches `localStorage`/`sessionStorage`/
 * cookies — state lives in memory only and is naturally cleared on reload,
 * matching "the SPA never sees or stores tokens" (it doesn't persist even
 * the non-token session summary).
 */
export type SessionState =
  | { readonly status: 'unauthenticated' }
  /** A "pre-onboarding" session (F0.5, S0.8): the BFF's `POST /auth/login`
   * establishes a real server-side session for a verified-but-membership-less
   * user even though the request itself reports `409 onboarding_required` —
   * see `apps/api/src/modules/bff/routes.ts` `requireOnboardingUser`. That
   * session carries NO `accountContext`, so it cannot be represented as
   * `SessionInfo`/`authenticated` (the contract requires `accountContext`,
   * see `no-tokens-in-browser.test.ts`) — but it DOES carry a live CSRF
   * double-submit token, delivered in the 409 response's `csrf-token`
   * HEADER (not a JSON body field, since the body there is a Problem). This
   * state exists so `getCsrfToken()` below can still hand that token to the
   * authed-fetch middleware for the one call this session is good for:
   * `POST /onboarding/account` (`<CreateCompanyPage>`). */
  | { readonly status: 'onboarding_required'; readonly csrfToken: string }
  | { readonly status: 'authenticated'; readonly session: AuthSession }

type Listener = (state: SessionState) => void

let state: SessionState = { status: 'unauthenticated' }
const listeners = new Set<Listener>()

function setState(next: SessionState): void {
  state = next
  for (const listener of listeners) {
    listener(state)
  }
}

export function getSessionState(): SessionState {
  return state
}

export function subscribeSessionState(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function setAuthenticated(session: AuthSession): void {
  setState({ status: 'authenticated', session })
}

/** See the `onboarding_required` `SessionState` variant's doc comment above. */
export function setOnboardingRequired(csrfToken: string): void {
  setState({ status: 'onboarding_required', csrfToken })
}

export function setUnauthenticated(): void {
  setState({ status: 'unauthenticated' })
}

/**
 * Called by the authed-fetch middleware when an API call comes back 401 for
 * a session that WAS authenticated — i.e. the session expired or was revoked
 * mid-use, as opposed to the ordinary "not logged in yet" 401 from the
 * initial `GET /auth/session` probe. A no-op (no listener notification) when
 * already unauthenticated, so a burst of concurrent 401s from in-flight
 * requests collapses into a single transition instead of re-triggering
 * whatever redirect-to-login side effect a subscriber attaches — avoiding a
 * refresh/redirect loop.
 */
export function invalidateSession(): void {
  if (state.status === 'unauthenticated') {
    return
  }
  setState({ status: 'unauthenticated' })
}

export function getCsrfToken(): string | undefined {
  if (state.status === 'authenticated') {
    return state.session.csrfToken
  }
  if (state.status === 'onboarding_required') {
    return state.csrfToken
  }
  return undefined
}
